import React, {ReactElement} from 'react';
import {classes, style} from './doppe-action-content-section-map.st.css';
import {DoppeActionContentSection} from "../../../client-server-common/types/doppe-action-content-section";
import {IDoppeViewerActionContext} from "../../../doppe-sdk";
import {DoppeViewerMap} from "../../doppe-viewer-map/doppe-viewer-map";
import {
    doppeViewerActionContextOpenModal
} from "../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils";
import {DoppeViewerModalPanelLayout} from "../../doppe-viewer-modal-panel/doppe-viewer-modal-panel";
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";
import {doppeHideableValueIsVisible} from "../../../client-server-common";
import {DoppeViewerMapDisplayAddress} from "../../doppe-viewer-map/doppe-viewer-map-display-address/doppe-viewer-map-display-address";
import {useEventCallback} from "@wix/devzai-utils-react";
import {DoppeViewerAppIconDismissMedium} from "../../doppe-viewer-icons/doppe-viewer-icons";
import {uiPartsGroupCreateMarking} from "@wix/devzai-utils-common";
import {DoppePageViewerPartsGroups} from "../../doppe-page-viewer-part/doppe-page-viewer-part";
import {DoppeViewerHorizontalButton} from "../../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button";

export const DoppeActionContentSectionMap = React.memo(function DoppeActionContentSectionMap (props: DoppeActionContentSectionMap.Props) {
    const {
        className,
        actionContext,
        contentSection,
        googleMapsApiKey,
        ...htmlAttributes
    } = props;

    const openModal = useEventCallback((modalContentGetter: (closeFunction: () => void) => ReactElement) => doppeViewerActionContextOpenModal(
        actionContext,
        (options) => {
            const closeFunction = () => options.modalController.closeWindow();
            const modalContent = modalContentGetter(closeFunction);
            
            return {
                render: () => <div className={style(classes.modalContent)}>
                    <DoppeViewerHorizontalButton
                        className={style(classes.dismissButton, {})}
                        prefixIcon={DoppeViewerAppIconDismissMedium}
                        textual={true}
                        {...uiPartsGroupCreateMarking(DoppePageViewerPartsGroups.CloseButton)}
                        onClick={closeFunction}
                    />
                    {modalContent}
                </div>,
                dependencies: []
            }
        },
        {
            mobileLayout: DoppeViewerModalPanelLayout.FullScreen,
            desktopLayout: DoppeViewerModalPanelLayout.FullScreen,
            showHeader: false,
        }
    ));

    const texts = useDoppeViewerLocales();
    const {address: {locationCoordinates, displayAddress}} = contentSection ?? {address: {}};
    const languageCode = navigator.language;
    const previewZoom = 10;
    const modalZoom = 13;

    if (!locationCoordinates || !languageCode) {
        return null;
    }

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            <DoppeViewerMap
                locationCoordinates={locationCoordinates}
                languageCode={languageCode}
                googleMapsApiKey={googleMapsApiKey}
                openModal={openModal}
                previewZoom={previewZoom}
                modalZoom={modalZoom}
                openModalText={texts['mapAction.buttonTitles.expandMap']}
                navigateToText={texts['mapAction.buttonTitles.navigateTo']}
                closeText={texts['mapAction.buttonTitles.close']}
            />
            {doppeHideableValueIsVisible(displayAddress) ? <DoppeViewerMapDisplayAddress
                locationCoordinates={locationCoordinates}
                addressTitle={texts['mapAction.address.title']}
                displayAddress={displayAddress}
            /> : null}
        </div>
    );
});

export namespace DoppeActionContentSectionMap {

    export interface Props extends React.HTMLAttributes<any> {
        actionContext: IDoppeViewerActionContext;
        contentSection: DoppeActionContentSection.MapSettings;
        googleMapsApiKey: string;
    }
}
